import TaskDueDatePicker from "../components/TaskDueDatePicker";
import "../../../scss/adminOffice/components/office-tasks-AO.scss";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../helpers/axios";

import { STONE_FONTS, epitaphChars, typoOf } from "./stoneFonts";
import { BLANK_CHECKS, BLANK_OPTIONS, fetchShotThumbBlob, getApiError, pictureUrl, saveBlankDefaults } from "./orderDocumentApi";

const clamp = (value, min, max) => Math.min(max, Math.max(min, Math.round(value * 100) / 100));

/** Кнопки-стрелки как в 3D-модификаторах: клик — шаг, удержание — повтор. */
function PadButton({ title, disabled, onStep, children }) {
    const timer = React.useRef(null);
    const stop = () => {
        clearTimeout(timer.current);
        clearInterval(timer.current);
        timer.current = null;
    };
    useEffect(() => stop, []);
    return (
        <button
            type="button"
            title={title}
            disabled={disabled}
            onClick={onStep}
            onPointerDown={() => {
                stop();
                // Удержание: после паузы шаги повторяются.
                timer.current = setTimeout(() => {
                    timer.current = setInterval(onStep, 140);
                }, 350);
            }}
            onPointerUp={stop}
            onPointerLeave={stop}
        >
            {children}
        </button>
    );
}

/**
 * Пульт: стрелки двигают, «+ / −» — крупнее / мельче, «•» — сброс.
 * move(dx, dy), zoom(delta), reset().
 */
function ArrowPad({ title, hint, disabled, move, zoom, reset, value, extra = null }) {
    return (
        <div className={`order-blank-AO__pad ${disabled ? "is-disabled" : ""}`}>
            <div className="order-blank-AO__pad-head">
                <b>{title}</b>
                {value ? <small>{value}</small> : null}
            </div>
            <div className="order-blank-AO__pad-grid">
                <span />
                <PadButton title="Вверх" disabled={disabled} onStep={() => move(0, -1)}>↑</PadButton>
                <span />
                <PadButton title="Крупнее" disabled={disabled} onStep={() => zoom(1)}>+</PadButton>
                <PadButton title="Влево" disabled={disabled} onStep={() => move(-1, 0)}>←</PadButton>
                <button type="button" title="Сбросить" disabled={disabled} onClick={reset}>•</button>
                <PadButton title="Вправо" disabled={disabled} onStep={() => move(1, 0)}>→</PadButton>
                <PadButton title="Мельче" disabled={disabled} onStep={() => zoom(-1)}>−</PadButton>
                <span />
                <PadButton title="Вниз" disabled={disabled} onStep={() => move(0, 1)}>↓</PadButton>
                <span />
            </div>
            {extra}
            {hint ? <small className="order-blank-AO__pad-hint">{hint}</small> : null}
        </div>
    );
}

/** Фото: стрелки сдвигают камень, + / − увеличивают; по умолчанию — на всю высоту, по центру. */
function PhotoPad({ side, onPatch }) {
    return (
        <ArrowPad
            title="Фото"
            value={`${Math.round((side.photoZoom || 1) * 100)}%`}
            hint="+ / − крупнее и мельче (можно меньше 100%) · • — на всю высоту по центру"
            move={(dx, dy) => onPatch((prev) => ({
                photoX: clamp((prev.photoX ?? 50) - dx * 3, 0, 100),
                photoY: clamp((prev.photoY ?? 50) - dy * 3, 0, 100),
            }))}
            zoom={(dir) => onPatch((prev) => ({ photoZoom: clamp((prev.photoZoom || 1) + dir * 0.05, 0.3, 4) }))}
            reset={() => onPatch(() => ({ photoZoom: 1, photoX: 50, photoY: 50 }))}
        />
    );
}

/** Надпись: ↑ ↓ — выше / ниже, ← → — уже / шире, + / − — размер. */
function TextPad({ side, disabled, multi, onPatch, title = "ФИО и даты", defaultY = 38 }) {
    return (
        <ArrowPad
            title={title}
            value={`${Math.round((side.textScale || 1) * 100)}%`}
            hint="Стрелки двигают надпись · + − размер · • сброс"
            disabled={disabled}
            move={(dx, dy) => onPatch((prev) => ({
                textY: clamp((prev.textY ?? defaultY) + dy * 2, 0, 95),
                textX: clamp((prev.textX ?? 0) + dx * 2, -50, 50),
            }))}
            zoom={(dir) => onPatch((prev) => ({ textScale: clamp((prev.textScale || 1) + dir * 0.05, 0.3, 2.5) }))}
            reset={() => onPatch(() => ({ textScale: 1, textY: defaultY, textX: 0, ...(multi ? { textWidth: 90 } : {}) }))}
            extra={multi ? (
                <div className="order-blank-AO__pad-extra">
                    <span>Расстояние между ФИО</span>
                    <PadButton title="Ближе" disabled={disabled} onStep={() => onPatch((prev) => ({ textWidth: clamp((prev.textWidth ?? 90) - 4, 20, 100) }))}>⟩⟨</PadButton>
                    <PadButton title="Дальше" disabled={disabled} onStep={() => onPatch((prev) => ({ textWidth: clamp((prev.textWidth ?? 90) + 4, 20, 100) }))}>⟨⟩</PadButton>
                </div>
            ) : null}
        />
    );
}

/** Превью снимка 3D-сцены (закрытый файл — через axios). */
function ShotImage({ officeId, shot }) {
    const [src, setSrc] = useState("");
    useEffect(() => {
        let url = "";
        let alive = true;
        fetchShotThumbBlob(officeId, shot.sessionCode, shot.id)
            .then((blob) => {
                if (!alive) return;
                url = URL.createObjectURL(blob);
                setSrc(url);
            })
            .catch(() => undefined);
        return () => {
            alive = false;
            if (url) URL.revokeObjectURL(url);
        };
    }, [officeId, shot.sessionCode, shot.id]);
    return src ? <img src={src} alt={shot.title} /> : <span>…</span>;
}

/** Выбор картинки стороны: без картинки / снимок сцены / фото позиции заказа. */
function ImagePicker({ officeId, doc, value, onChange, shotsOnly = false }) {
    const products = shotsOnly ? [] : doc.items.filter((item) => item.kind === "product" && item.picture);
    const isActive = (type, id) => value?.type === type && String(value?.id || "") === String(id || "");
    return (
        <div className="order-blank-AO__pics">
            <button type="button" className={!value || value.type === "none" ? "is-active" : ""} onClick={() => onChange({ type: "none", id: "" })}>
                <span>Без фото</span>
            </button>
            {["front", "back"].filter((side) => doc.sceneShots?.[side]?.src).map((side) => (
                <button type="button" key={`scene-${side}`} className={isActive("scene", side) ? "is-active" : ""} title="Автоснимок из 3D-сцены" onClick={() => onChange({ type: "scene", id: side })}>
                    <img src={doc.sceneShots[side].src} alt="" />
                    <small>{side === "front" ? "сцена · перед" : "сцена · зад"}</small>
                </button>
            ))}
            {doc.shots.map((shot) => (
                <button type="button" key={`s-${shot.id}`} className={isActive("shot", shot.id) ? "is-active" : ""} title={shot.title} onClick={() => onChange({ type: "shot", id: shot.id })}>
                    <ShotImage officeId={officeId} shot={shot} />
                    <small>3D</small>
                </button>
            ))}
            {products.map((item) => (
                <button type="button" key={`p-${item.n}`} className={isActive("product", item.n) ? "is-active" : ""} title={item.title} onClick={() => onChange({ type: "product", id: String(item.n), src: pictureUrl(item.picture) })}>
                    <img src={pictureUrl(item.picture)} alt={item.title} />
                    <small>{item.n}</small>
                </button>
            ))}
        </div>
    );
}

/** Число с кнопками − / +: сразу меняет лист. */
function Num({ value, step, min, max, onChange }) {
    const round = (v) => Math.round(v * 100) / 100;
    const apply = (v) => onChange(round(Math.min(max, Math.max(min, v))));
    return (
        <div className="order-blank-AO__num">
            <button type="button" onClick={() => apply(Number(value) - step)}>−</button>
            <input
                type="number"
                value={value}
                step={step}
                min={min}
                max={max}
                onChange={(event) => {
                    const v = Number(String(event.target.value).replace(",", "."));
                    if (Number.isFinite(v)) apply(v);
                }}
            />
            <button type="button" onClick={() => apply(Number(value) + step)}>+</button>
        </div>
    );
}

/** Блок бланка: свёрнут по умолчанию, в шапке — коротко, что заполнено. */
function Section({ title, summary, children, invalid = false }) {
    const [open, setOpen] = useState(false);
    useEffect(() => { if (invalid) setOpen(true); }, [invalid]);
    return (
        <section className={`order-blank-AO__sec ${open ? "is-open" : ""}`} style={invalid ? { borderColor: "var(--ui-danger, #dc2626)" } : undefined}>
            <button type="button" className="order-blank-AO__sec-head" onClick={() => setOpen((value) => invalid ? true : !value)}>
                <i>{open ? "▾" : "▸"}</i>
                <b>{title}</b>
                <small>{summary || "не заполнено"}</small>
            </button>
            {open ? <div className="order-blank-AO__sec-body">{children}</div> : null}
        </section>
    );
}

const PERSON_MODES = [
    ["single", "Одиночный"],
    ["double", "Двойной"],
    ["triple", "Тройной"],
];

// «Умный монтаж»: изделия сцены, разрешённые WRK и проверка приходят с сервера
// (document.installation). Тариф, количество и сумма пересчитываются там же.
const numberWork = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const roundWork = (value) => Math.round(numberWork(value) * 100) / 100;
const round3 = (value) => Math.round(numberWork(value) * 1000) / 1000;
const normWorkText = (value) => String(value || '').toLocaleLowerCase('ru-RU').replace(/ё/g, 'е').trim();
const money = (value) => `${numberWork(value).toLocaleString('ru-RU')} ₽`;
const newWorkId = () => `wrk-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const TYPE_WORDS = { monument: /памятник|стел/, fence: /оград|секци|столб/, bench: /скам|лавк/, table: /стол(?!б)/, tile: /плитк/, complex: /комплекс/ };
const INSTALL_MODES = [
    ['install', 'Установка'],
    ['dismantle_install', 'Демонтаж и установка'],
    ['dismantle', 'Только демонтаж'],
    ['none', 'Без установки'],
];
const STATUS_LABEL = { pending: 'Ожидает подтверждения', confirmed: 'Подтверждено', declined: 'Не требуется' };

/** Короткая подпись блока: одинаково на сайте и после F5. */
export const signatureOf = (value) => {
    const textValue = JSON.stringify(value ?? null);
    let hash = 5381;
    for (let i = 0; i < textValue.length; i += 1) hash = ((hash << 5) + hash + textValue.charCodeAt(i)) | 0;
    return `s${(hash >>> 0).toString(36)}${textValue.length.toString(36)}`;
};
export const datesSignature = (draft = {}) => signatureOf([
    draft.blank?.installPeriod, draft.blank?.showMonth, draft.blank?.installPlace, draft.blank?.deliveryPlace, draft.blank?.showPrices,
    ...['sector', 'plot', 'burialPlace', 'geoLabel', 'geoLat', 'geoLng', 'comment'].map((key) => draft.overrides?.[key] || ''),
    draft.acceptedBy || '',
]);
export const worksSignature = (draft = {}) => signatureOf([
    draft.checkout?.installWork || '',
    (draft.checkout?.installationWorks || []).map((w) => [w.workInstanceId, w.templateId, w.sceneInstanceId, w.status, round3(w.quantity), roundWork(w.unitPrice), !!w.quantityOverride]),
]);
export const sectionConfirmed = (draft, key) => {
    const saved = draft?.sections?.[key];
    if (!saved?.signature) return false;
    return saved.signature === (key === 'dates' ? datesSignature(draft) : worksSignature(draft));
};

const isDemolition = (template) => !!template?.demolition || /демонтаж|разбор|снос/.test(normWorkText(`${template?.category || ''} ${template?.name || ''}`));
const isInstall = (template) => /установ|монтаж/.test(normWorkText(template?.name)) && !isDemolition(template);
const foundationOnly = (template) => template?.foundationScope === 'slab' && !isInstall(template);

const autoQuantity = (template, object) => {
    const g = object?.geometry || {};
    if (template?.pricingMode === 'volume') return g.volumeM3 > 0 ? round3(g.volumeM3) : template.customVolume > 0 ? round3(template.customVolume) : null;
    if (template?.pricingMode === 'area') return g.areaM2 > 0 ? round3(g.areaM2) : null;
    if (template?.pricingMode === 'post') return template.postsCount > 0 ? Math.round(template.postsCount) : null;
    return 1;
};

/** Новая/перепривязанная работа: количество по геометрии, цена — тариф шаблона. */
const buildWork = (template, object, extra = {}) => {
    const quantity = autoQuantity(template, object);
    const unitPrice = template.pricingMode === 'manual' ? 0 : roundWork(template.rate);
    return {
        workInstanceId: newWorkId(), templateId: template.id, sceneInstanceId: object?.instanceId || '',
        kind: isDemolition(template) ? 'demolition' : foundationOnly(template) ? 'foundation' : isInstall(template) ? 'installation' : 'manual',
        source: 'manual', status: 'pending', quantity: quantity ?? 0, quantityOverride: false,
        unitPrice, totalPrice: roundWork((quantity ?? 0) * unitPrice), geometry: object?.geometry || {}, note: '',
        ...extra,
    };
};

/** Насколько шаблон подходит изделию (только среди разрешённых категорией). */
const installScore = (template, object) => {
    if (isDemolition(template) || foundationOnly(template)) return -1;
    if (object.type === 'monument' && object.foundation && template.foundationScope === 'strip') return -1;
    const name = normWorkText(template.name);
    let score = isInstall(template) ? 10 : 0;
    if (TYPE_WORDS[object.type]?.test(name)) score += 15;
    Object.entries(TYPE_WORDS).forEach(([type, regex]) => { if (type !== object.type && regex.test(name)) score -= 15; });
    if ((template.targetCategories || []).includes(object.type)) score += 8;
    if (object.type === 'monument') {
        if (template.foundationScope === 'slab') score += object.foundation ? 30 : -30;
        if (template.geometryType === 'granite_legs') score += object.foundation ? -25 : 20;
    }
    return score;
};

function SmartInstallationWorks({ doc, draft, onField, validation = {} }) {
    const installation = doc?.installation || null;
    const objects = React.useMemo(() => installation?.objects || [], [installation]);
    const templates = React.useMemo(() => installation?.templates || [], [installation]);
    const issues = installation?.issues || [];
    const checkout = draft.checkout || {};
    const selected = React.useMemo(() => (Array.isArray(checkout.installationWorks) ? checkout.installationWorks : []), [checkout.installationWorks]);
    const mode = checkout.installWork || '';
    const setWorks = (next) => onField('checkout', 'installationWorks', next);
    const templateOf = (id) => templates.find((entry) => String(entry.id) === String(id));
    const objectOf = (id) => objects.find((entry) => entry.instanceId === String(id || ''));
    const allowedFor = (object) => {
        if (!object) return templates;
        const ids = new Set([...(object.allowedWorkIds || []), ...(object.allowedDemolitionIds || [])]);
        return templates.filter((template) => ids.has(String(template.id)));
    };
    const [addObject, setAddObject] = useState('');
    const [addTemplate, setAddTemplate] = useState('');

    // Тариф шаблона или геометрия изделия изменились — подтверждение снимается.
    useEffect(() => {
        if (!installation) return;
        let changed = false;
        const next = selected.map((work) => {
            const template = templateOf(work.templateId);
            if (!template || work.status === 'declined') return work;
            const object = objectOf(work.sceneInstanceId);
            const patch = {};
            if (template.pricingMode !== 'manual' && roundWork(work.unitPrice) !== roundWork(template.rate)) patch.unitPrice = roundWork(template.rate);
            const auto = autoQuantity(template, object);
            if (!work.quantityOverride && ['volume', 'area'].includes(template.pricingMode) && auto !== null && round3(work.quantity) !== auto) patch.quantity = auto;
            if (!Object.keys(patch).length) return work;
            changed = true;
            const merged = { ...work, ...patch, geometry: object?.geometry || work.geometry || {} };
            return { ...merged, totalPrice: roundWork(numberWork(merged.quantity) * numberWork(merged.unitPrice)), status: 'pending', note: 'Тариф или размеры изменились — проверьте и подтвердите' };
        });
        if (changed) setWorks(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installation, selected]);

    // Работа, выбранная в 3D («Комплект памятника → Заливка → Монтаж с основанием»):
    // попадает сюда сама — подтверждённой, если у шаблона есть тариф.
    // Снята в 3D — убирается; меняется только в 3D.
    useEffect(() => {
        if (!installation || mode === 'none') return;
        let changed = false;
        let next = [...selected];
        objects.forEach((object) => {
            // Установка WRK уже посчитана в комплекте памятника (строка заливки с ценой) —
            // в «Работы на месте» второй раз не добавляем, иначе двойное начисление.
            const templateId = object.foundation?.priced ? '' : String(object.foundation?.workTemplateId || '');
            const index = next.findIndex((work) => work.source === 'scene' && work.sceneInstanceId === object.instanceId);
            if (!templateId) {
                if (index >= 0) { next.splice(index, 1); changed = true; }
                return;
            }
            if (index >= 0 && String(next[index].templateId) === templateId) return;
            const template = templateOf(templateId);
            if (!template) return;
            const work = buildWork(template, object, { source: 'scene', kind: 'installation' });
            const ready = template.pricingMode !== 'manual' && numberWork(template.rate) > 0 && numberWork(work.quantity) > 0;
            const row = { ...work, status: ready ? 'confirmed' : 'pending', note: ready ? '' : 'Выбрано в 3D — укажите цену и подтвердите' };
            // Неподтверждённые предложения установки для этого изделия заменяет выбор из 3D.
            next = next.filter((entry) => !(entry.sceneInstanceId === object.instanceId && entry.source !== 'scene' && entry.status === 'pending' && entry.kind === 'installation'));
            const at = next.findIndex((entry) => entry.source === 'scene' && entry.sceneInstanceId === object.instanceId);
            if (at >= 0) next[at] = row; else next.push(row);
            changed = true;
        });
        if (changed) setWorks(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installation, mode]);

    const proposals = React.useMemo(() => {
        if (!installation || mode === 'none') return [];
        const result = [];
        objects.forEach((object) => {
            const bound = selected.filter((work) => work.sceneInstanceId === object.instanceId && work.status !== 'removed');
            const choices = templates.filter((template) => (object.allowedWorkIds || []).includes(String(template.id)));
            if (!choices.length) return;
            let installTemplate = null;
            if (!bound.some((work) => work.kind !== 'demolition' && work.kind !== 'foundation')) {
                installTemplate = choices.filter((template) => installScore(template, object) >= 0)
                    .sort((a, b) => installScore(b, object) - installScore(a, object))[0] || null;
                if (installTemplate) result.push(buildWork(installTemplate, object, { source: 'auto', kind: 'installation' }));
            }
            // Сплошная заливка без своей цены: отдельная работа на основание,
            // если выбранная установка его не включает.
            const covered = [...bound.map((work) => templateOf(work.templateId)), installTemplate].some((template) => template?.foundationScope === 'slab');
            if (object.type === 'monument' && object.foundation && !object.foundation.priced && !covered && !bound.some((work) => work.kind === 'foundation')) {
                const pour = choices.find(foundationOnly);
                if (pour) result.push(buildWork(pour, object, { source: 'auto', kind: 'foundation' }));
            }
        });
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installation, objects, templates, selected, mode]);

    const update = (id, patch) => setWorks(selected.map((work) => (work.workInstanceId === id ? { ...work, ...patch } : work)));
    const edit = (work, patch) => {
        const merged = { ...work, ...patch };
        update(work.workInstanceId, { ...patch, totalPrice: roundWork(numberWork(merged.quantity) * numberWork(merged.unitPrice)), status: 'pending', note: '' });
    };
    const rebind = (work, instanceId) => {
        const template = templateOf(work.templateId);
        const object = objectOf(instanceId);
        if (!template) return update(work.workInstanceId, { sceneInstanceId: instanceId, status: 'pending' });
        const fresh = buildWork(template, object);
        update(work.workInstanceId, { sceneInstanceId: instanceId, quantity: fresh.quantity, quantityOverride: false, totalPrice: roundWork(fresh.quantity * numberWork(work.unitPrice || fresh.unitPrice)), geometry: fresh.geometry, status: 'pending', note: '' });
    };
    const issueOf = (id) => issues.filter((issue) => issue.workInstanceId === id);
    const problemOf = (work, template, object) => {
        if (!template) return 'Шаблон удалён из каталога офиса';
        if (work.sceneInstanceId && !object) return 'Изделия больше нет в комплекте — привяжите заново';
        if (!(numberWork(work.quantity) > 0)) return 'Укажите количество';
        if (template.pricingMode === 'manual' && !(numberWork(work.unitPrice) > 0)) return 'Ручная оценка: укажите цену больше 0';
        if (template.pricingMode !== 'manual' && !(numberWork(template.rate) > 0)) return `В шаблоне ${template.article} не задан тариф — настройте каталог работ`;
        if (template.pricingMode === 'post' && !Number.isInteger(numberWork(work.quantity))) return 'Количество столбов — целое число';
        if (object?.type === 'monument' && object.foundation && template.foundationScope === 'strip') return 'Ленточная заливка не подходит к сплошной заливке сцены';
        return '';
    };
    const setMode = (value) => {
        if (value === 'none' && selected.some((work) => work.status !== 'declined')) {
            if (!window.confirm('Отметить «Без установки»? Все работы будут помечены «Не требуется».')) return;
            onField('checkout', 'installationWorks', selected.map((work) => ({ ...work, status: 'declined' })));
        }
        onField('checkout', 'installWork', value);
    };
    const coverOf = (object) => selected.find((work) => work.sceneInstanceId === object.instanceId && work.status === 'confirmed' && templateOf(work.templateId)?.foundationScope === 'slab');
    const addOptions = allowedFor(objectOf(addObject));
    const unconfigured = objects.filter((object) => !object.configured || !(object.allowedWorkIds || []).length);
    const confirmedTotal = selected.filter((work) => work.status === 'confirmed').reduce((sum, work) => sum + numberWork(work.totalPrice), 0);

    if (!installation) return <div className="smart-works-AO is-wide"><small className="smart-works-AO__hint">Загружаем изделия и работы…</small></div>;

    return <div className="smart-works-AO is-wide">
        <div className="smart-works-AO__modes" role="radiogroup" aria-label="Работы на месте" aria-invalid={!!validation.installWork}>
            {INSTALL_MODES.map(([value, label]) => <button type="button" key={value} role="radio" aria-checked={mode === value} className={`smart-works-AO__mode${mode === value ? ' is-active' : ''}`} onClick={() => setMode(value)}>{label}</button>)}
        </div>
        {mode === 'none' ? <small className="smart-works-AO__hint">Установка не требуется — решение сохранено. Работы не начисляются.</small> : <>
            <div className="smart-works-AO__objects">
                {objects.length ? objects.map((object) => {
                    const cover = coverOf(object);
                    return <div key={object.instanceId} className="smart-works-AO__object">
                        <span className="smart-works-AO__chip">{object.typeLabel}</span>
                        <b>{object.title}</b>
                        {object.foundation ? <small>
                            Заливка {object.foundation.widthMm}×{object.foundation.lengthMm}×{object.foundation.heightMm} мм · {object.foundation.priced ? `своя цена ${money(object.foundation.ownSum)}` : cover ? `включена в ${templateOf(cover.templateId)?.article || 'WRK'}` : 'без цены — нужна работа с основанием'}
                        </small> : null}
                        {object.geometry?.perimeterM ? <small>{String(object.geometry.perimeterM).replace('.', ',')} пог. м</small> : null}
                        {!object.configured || !(object.allowedWorkIds || []).length ? <small className="is-warn">Нет разрешённых WRK — настройки категории «{object.categoryName || object.title}» → «Монтаж»</small> : null}
                    </div>;
                }) : <small className="smart-works-AO__hint">В комплекте нет изделий для монтажа.</small>}
            </div>
            {!installation.rulesLoaded ? <small className="smart-works-AO__issue">Не удалось загрузить настройки офиса для монтажа.</small> : null}
            {unconfigured.length ? <small className="smart-works-AO__hint">Работы для {unconfigured.map((object) => object.title).join(', ')} не назначены — автоподбор их пропустит.</small> : null}
            <div className="smart-works-AO__actions">
                <button type="button" className="smart-works-AO__btn is-primary" disabled={!proposals.length} onClick={() => setWorks([...selected, ...proposals])}>
                    {proposals.length ? `Предложить работы по сцене (${proposals.length})` : 'Все изделия уже с работами'}
                </button>
                {selected.length ? <span className="smart-works-AO__sum">Подтверждено: <b>{money(confirmedTotal)}</b></span> : null}
            </div>
            <div className="smart-works-AO__list">
                {selected.map((work) => {
                    const template = templateOf(work.templateId);
                    const object = objectOf(work.sceneInstanceId);
                    const problem = work.status === 'declined' ? '' : problemOf(work, template, object);
                    const serverIssues = issueOf(work.workInstanceId);
                    const manual = template?.pricingMode === 'manual';
                    const unit = template?.unitLabel || 'шт';
                    const auto = template ? autoQuantity(template, object) : null;
                    const bindOptions = objects.filter((entry) => !template || allowedFor(entry).some((t) => String(t.id) === String(template.id)) || entry.instanceId === work.sceneInstanceId);
                    return <article key={work.workInstanceId} className={`smart-works-AO__card is-${work.status}${problem || serverIssues.some((i) => i.level === 'error') ? ' has-error' : ''}`}>
                        <header className="smart-works-AO__card-head">
                            <div>
                                <small>{template?.article || 'WRK'}{template?.category ? ` · ${template.category}` : ''}{work.source === 'auto' ? ' · предложено по сцене' : work.source === 'scene' ? ' · выбрано в 3D' : ''}</small>
                                <b>{template?.name || 'Шаблон недоступен'}</b>
                            </div>
                            <span className={`smart-works-AO__status is-${work.status}`}>{STATUS_LABEL[work.status] || work.status}</span>
                        </header>
                        <div className="smart-works-AO__fields">
                            <label><span>Изделие</span>
                                <select value={work.sceneInstanceId || ''} onChange={(event) => rebind(work, event.target.value)}>
                                    <option value="">Весь участок</option>
                                    {bindOptions.map((entry) => <option key={entry.instanceId} value={entry.instanceId}>{entry.typeLabel}: {entry.title}</option>)}
                                    {work.sceneInstanceId && !object ? <option value={work.sceneInstanceId}>— изделие удалено —</option> : null}
                                </select>
                            </label>
                            <label><span>Количество, {unit}</span>
                                <input type="number" min="0" step={template?.pricingMode === 'post' ? 1 : 0.001} value={work.quantity} onChange={(event) => edit(work, { quantity: Number(event.target.value), quantityOverride: ['volume', 'area'].includes(template?.pricingMode) })} />
                            </label>
                            <label><span>Цена за {unit}</span>
                                <input type="number" min="0" readOnly={!manual} value={work.unitPrice} onChange={(event) => edit(work, { unitPrice: Number(event.target.value) })} />
                            </label>
                            <div className="smart-works-AO__total"><span>Итого</span><b>{money(work.totalPrice)}</b></div>
                        </div>
                        {work.quantityOverride && auto !== null ? <small className="smart-works-AO__hint">Количество задано вручную. <button type="button" className="smart-works-AO__link" onClick={() => edit(work, { quantity: auto, quantityOverride: false })}>Вернуть по сцене ({String(auto).replace('.', ',')} {unit})</button></small> : null}
                        {object?.foundation && template?.foundationScope === 'slab' ? <small className="smart-works-AO__hint">Включает заливку {object.foundation.widthMm}×{object.foundation.lengthMm}×{object.foundation.heightMm} мм — в бланке «включено в монтаж»{object.foundation.priced ? ', но у заливки есть своя цена: проверьте двойное начисление' : ''}.</small> : null}
                        {work.note ? <small className="smart-works-AO__issue is-warn">{work.note}</small> : null}
                        {problem ? <small className="smart-works-AO__issue">{problem}</small> : null}
                        {serverIssues.map((issue, index) => <small key={index} className={`smart-works-AO__issue${issue.level === 'warning' ? ' is-warn' : ''}`}>{issue.message}</small>)}
                        <footer className="smart-works-AO__buttons">
                            {work.status === 'confirmed'
                                ? <button type="button" className="smart-works-AO__btn" onClick={() => update(work.workInstanceId, { status: 'pending' })}>Изменить</button>
                                : <button type="button" className="smart-works-AO__btn is-primary" disabled={!!problem} onClick={() => update(work.workInstanceId, { status: 'confirmed', note: '' })}>Подтвердить</button>}
                            {work.status !== 'declined' ? <button type="button" className="smart-works-AO__btn" onClick={() => update(work.workInstanceId, { status: 'declined', note: '' })}>Не требуется</button> : null}
                            <button type="button" className="smart-works-AO__btn is-danger" onClick={() => setWorks(selected.filter((entry) => entry.workInstanceId !== work.workInstanceId))}>Удалить</button>
                        </footer>
                    </article>;
                })}
            </div>
            <div className="smart-works-AO__add">
                <label><span>Изделие</span>
                    <select value={addObject} onChange={(event) => { setAddObject(event.target.value); setAddTemplate(''); }}>
                        <option value="">Весь участок</option>
                        {objects.map((object) => <option key={object.instanceId} value={object.instanceId}>{object.typeLabel}: {object.title}</option>)}
                    </select>
                </label>
                <label><span>Шаблон WRK</span>
                    <select value={addTemplate} onChange={(event) => setAddTemplate(event.target.value)}>
                        <option value="">{addOptions.length ? 'Выберите работу…' : 'Нет разрешённых работ'}</option>
                        {['Монтаж', 'Демонтаж'].map((group) => {
                            const list = addOptions.filter((template) => (group === 'Демонтаж') === isDemolition(template));
                            return list.length ? <optgroup key={group} label={group}>{list.map((template) => <option key={template.id} value={template.id}>{template.article} — {template.name}</option>)}</optgroup> : null;
                        })}
                    </select>
                </label>
                <button type="button" className="smart-works-AO__btn" disabled={!addTemplate} onClick={() => { const template = templateOf(addTemplate); if (template) setWorks([...selected, buildWork(template, objectOf(addObject))]); setAddTemplate(''); }}>Добавить работу</button>
            </div>
            <small className="smart-works-AO__hint">Автоподбор только предлагает — стоимость попадает в заказ после «Подтвердить». Изменение количества, цены, тарифа или размеров снимает подтверждение.</small>
        </>}
    </div>;
}

/** Блок с отдельным «Сохранить и подтвердить»: сворачивается после подтверждения, восстанавливается после F5. */
function ConfirmSection({ title, summary, invalid = false, confirmed, touched, onConfirm, children }) {
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => { if (invalid) setOpen(true); }, [invalid]);
    const confirm = () => {
        const message = onConfirm();
        setError(message || '');
        if (!message) setOpen(false);
    };
    return (
        <section className={`order-blank-AO__sec ${open ? "is-open" : ""} ${confirmed ? "is-confirmed" : ""} ${invalid ? "is-invalid" : ""}`}>
            <button type="button" className="order-blank-AO__sec-head" onClick={() => setOpen((value) => !value)}>
                <i>{open ? "▾" : "▸"}</i>
                <b>{title}</b>
                <small>{summary || "не заполнено"}</small>
                <em className={`order-blank-AO__badge ${confirmed ? "is-ok" : touched ? "is-warn" : ""}`}>{confirmed ? "✓ Подтверждено" : touched ? "Изменено" : "Не подтверждено"}</em>
            </button>
            {open ? <div className="order-blank-AO__sec-body">
                {children}
                <div className="order-blank-AO__confirm is-wide">
                    {error ? <small role="alert">{error}</small> : <small>{confirmed ? "Блок подтверждён. Любая правка снимет подтверждение только этого блока." : "Проверьте данные и подтвердите блок."}</small>}
                    <button type="button" className="smart-works-AO__btn is-primary" onClick={confirm}>{confirmed ? "Сохранено" : "Сохранить и подтвердить"}</button>
                </div>
            </div> : null}
        </section>
    );
}

/**
 * Эскиз бланка «Подробное описание работ»: всё, что печатается на листе.
 * blank — черновик эскиза, deceased — надпись (ФИО, даты, эпитафия), остальное — поля формы.
 */
export default function BlankEditor({ officeId, doc, draft, onBlank, onField, checkout = false, validation = {}, sceneLinked = false, onOpenScene }) {
    const blank = draft.blank;
    const [installationCatalog, setInstallationCatalog] = useState([]);
    const [installationCatalogError, setInstallationCatalogError] = useState("");
    useEffect(() => {
        let active = true;
        setInstallationCatalog([]);
        setInstallationCatalogError("");
        if (!officeId || officeId === "all" || checkout) return () => { active = false; };
        axiosInstance.get(`/office/installation-works/${officeId}`)
            .then(({ data }) => { if (active) setInstallationCatalog(Array.isArray(data?.works) ? data.works : []); })
            .catch(() => { if (active) setInstallationCatalogError("Не удалось загрузить шаблоны работ. Проверьте право «Товары»."); });
        return () => { active = false; };
    }, [officeId, checkout]);
    const INSTALL_NOTE_PREFIX = "Монтаж по шаблону: ";
    const setInstallationTemplate = (id) => onBlank((prev) => {
        const work = installationCatalog.find((entry) => String(entry.id) === String(id));
        const extras = (prev.extras || []).filter((line) => !String(line).startsWith(INSTALL_NOTE_PREFIX));
        if (!work) return { ...prev, extras, installationTemplateId: "" };
        const price = work.pricingMode === "manual" ? "стоимость уточняется" : `тариф ${work.rate} ₽${work.pricingMode === "volume" ? "/м³" : work.pricingMode === "area" ? "/м²" : work.pricingMode === "post" ? "/столб" : ""}, итог согласовать отдельно`;
        const description = [work.name, `арт. ${work.article}`, price].filter(Boolean).join(" · ");
        return { ...prev, installationTemplateId: work.id, extras: [...extras, `${INSTALL_NOTE_PREFIX}${description}`].slice(0, 12) };
    });
    const [defaultsState, setDefaultsState] = useState("");
    const saveDefaults = async () => {
        setDefaultsState("Сохраняем…");
        try {
            const pick = (side, keys) => keys.reduce((acc, key) => (side[key] === undefined ? acc : { ...acc, [key]: side[key] }), {});
            const data = await saveBlankDefaults(officeId, {
                front: pick(blank.front, ["photoZoom", "photoX", "photoY", "textScale", "textY", "textX", "textWidth", "overlayColor", "overlay", "font", "surnameSize", "nameSize", "datesSize", "fioLine", "fioLetter", "datesLine", "datesLetter", "epitaphFont", "epitaphSize", "epitaphLine", "epitaphLetter"]),
                back: pick(blank.back, ["photoZoom", "photoX", "photoY", "textScale", "textY", "textX"]),
            });
            setDefaultsState(data.message || "Сохранено");
        } catch (requestError) {
            setDefaultsState(getApiError(requestError));
        }
    };
    const set = (key, value) => onBlank((prev) => ({ ...prev, [key]: value }));
    const setIn = (group, key, value) => onBlank((prev) => ({ ...prev, [group]: { ...(prev[group] || {}), [key]: value } }));
    // Сразу несколько полей стороны (пульт): updater(prevSide) → изменения.
    const patchSide = (group) => (updater) => onBlank((prev) => ({ ...prev, [group]: { ...(prev[group] || {}), ...updater(prev[group] || {}) } }));
    const personsCount = blank.persons === "triple" ? 3 : blank.persons === "double" ? 2 : 1;
    const typo = typoOf(blank.front);
    const join = (...parts) => parts.filter(Boolean).join(" · ");
    const fio = (person = {}) => [blank.sharedSurnameEnabled && personsCount > 1 ? "" : person.surname, person.name, person.patronymic].filter(Boolean).join(" ");
    const imageLabel = (ref) => (ref?.type === "product" ? "фото товара" : ref?.type === "shot" ? "снимок 3D" : ref?.type === "scene" ? "снимок сцены" : "без фото");
    const summary = {
        head: join(`Договор № ${blank.contractNumber || doc.order.number}`, draft.overrides.customerName || doc.customer.name, draft.overrides.customerPhone || doc.customer.phone),
        text: join(
            personsCount > 1 ? (personsCount === 3 ? "тройной" : "двойной") : "",
            blank.sharedSurnameEnabled && personsCount > 1 ? blank.sharedSurname : "",
            [draft.deceased, ...(blank.extraPersons || [])].slice(0, personsCount).map(fio).filter(Boolean).join(", "),
            draft.deceased.dates
        ),
        front: join(
            personsCount > 1 ? (personsCount === 3 ? "тройной" : "двойной") : "",
            blank.sharedSurnameEnabled && personsCount > 1 ? blank.sharedSurname : "",
            [draft.deceased, ...(blank.extraPersons || [])].slice(0, personsCount).map(fio).filter(Boolean).join(", "),
            imageLabel(blank.front.image),
            blank.backEnabled ? "задняя стенка вкл" : ""
        ),
        back: join(blank.back.title, imageLabel(blank.back.image), blank.back.text ? "есть текст" : ""),
        stela: join(
            blank.title,
            `${blank.components.filter((row) => row.size).length} с размерами`,
            Object.values(blank.checks || {}).filter(Boolean).length ? `отмечено: ${Object.values(blank.checks).filter(Boolean).length}` : ""
        ),
        options: join(
            Object.values(blank.options || {}).filter((value) => String(value || "").trim()).length ? `заполнено: ${Object.values(blank.options).filter((value) => String(value || "").trim()).length}` : "",
            blank.extras.filter((line) => line.trim()).length ? `дополнений: ${blank.extras.filter((line) => line.trim()).length}` : ""
        ),
        dates: join(blank.installPeriod || "установочный период", blank.installPlace, draft.acceptedBy ? `принял ${draft.acceptedBy}` : ""),
    };
    const activeWorks = (draft.checkout?.installationWorks || []).filter((work) => work.status !== "declined" && work.status !== "removed");
    const worksSummary = draft.checkout?.installWork === "none" ? "Без установки"
        : activeWorks.length ? `${activeWorks.length} работ · подтверждено ${activeWorks.filter((work) => work.status === "confirmed").length}`
            : ({ install: "Установка", dismantle_install: "Демонтаж и установка", dismantle: "Только демонтаж" }[draft.checkout?.installWork] || "Уточнить установку");
    const setPerson = (index, key, value) => onBlank((prev) => {
        const list = [0, 1].map((i) => ({ surname: "", name: "", patronymic: "", dates: "", ...((prev.extraPersons || [])[i] || {}) }));
        list[index] = { ...list[index], [key]: value };
        return { ...prev, extraPersons: list };
    });
    // «Обновить из 3D-сцены»: состав, ФИО/даты, эпитафия, оформление — как в сцене сейчас.
    const sceneData = doc.scene || null;
    const applyScene = () => {
        if (!sceneData) return;
        if (!window.confirm("Заменить наименование, состав, ФИО/даты, эпитафию и оформление данными из 3D-сцены?")) return;
        const [first = {}, ...rest] = sceneData.persons || [];
        ["surname", "name", "patronymic", "dates"].forEach((key) => onField("deceased", key, first[key] || ""));
        onField("deceased", "epitaph", sceneData.epitaph || "");
        onBlank((prev) => ({
            ...prev,
            title: sceneData.title || prev.title,
            article: sceneData.article || prev.article,
            components: Array.isArray(sceneData.components) && sceneData.components.length ? sceneData.components : prev.components,
            persons: sceneData.personsMode || prev.persons,
            sharedSurnameEnabled: !!sceneData.sharedSurnameEnabled,
            sharedSurname: sceneData.sharedSurname || "",
            extraPersons: rest.slice(0, 2),
            epitaphSide: sceneData.epitaphSide || "front",
            backEnabled: prev.backEnabled || !!sceneData.backEnabled,
            back: { ...prev.back, text: sceneData.backText || prev.back.text },
            options: { ...prev.options, ...Object.fromEntries(Object.entries(sceneData.options || {}).filter(([, v]) => v)) },
        }));
    };
    const setFence = (key, field, value) => onBlank((prev) => ({
        ...prev,
        fences: { ...(prev.fences || {}), [key]: { ...((prev.fences || {})[key] || {}), [field]: value } },
    }));
    const setRow = (index, key, value) => onBlank((prev) => ({
        ...prev,
        components: prev.components.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)),
    }));

    const input = (label, value, onChange, placeholder = "", extra = {}) => (
        <label className={extra.wide ? "is-wide" : ""}>
            <span>{label}{extra.field && validation[extra.field] ? ` — ${validation[extra.field]}` : ""}</span>
            {extra.rows ? (
                <textarea rows={extra.rows} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
            ) : (
                <input aria-invalid={!!validation[extra.field]} style={validation[extra.field] ? { borderColor: "var(--ui-danger, #dc2626)" } : undefined} type={extra.type || "text"} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
            )}
        </label>
    );

    return (
        <div className="order-blank-AO">
            {sceneData ? (
                <div className="order-blank-AO__scene">
                    <span>Данные взяты из 3D-сцены: {[sceneData.title, sceneData.sharedSurnameEnabled ? sceneData.sharedSurname : (sceneData.persons || []).map((p) => p.surname).filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</span>
                    <button type="button" className="order-doc-AO__ghost" onClick={applyScene}>Обновить из 3D-сцены</button>
                </div>
            ) : null}
            <Section title="Шапка и заказчик" summary={summary.head} invalid={!!(validation.customerName || validation.customerPhone)}>
                {input("Договор №", blank.contractNumber, (v) => set("contractNumber", v), doc.order.number)}
                {input("Дата договора", blank.contractDate, (v) => set("contractDate", v), "как у заказа")}
                {input("ФИО заказчика", draft.overrides.customerName, (v) => onField("overrides", "customerName", v), doc.customer.name, { field: "customerName" })}
                {input("Телефон", draft.overrides.customerPhone, (v) => onField("overrides", "customerPhone", v), doc.customer.phone, { field: "customerPhone", type: "tel" })}
                {checkout ? input("Email", draft.overrides.customerEmail || "", (v) => onField("overrides", "customerEmail", v), "", { type: "email" }) : null}
                {input("Telegram / MAX", blank.messengerPhone, (v) => set("messengerPhone", v), "телефон для мессенджера")}
                {input("Адрес заказчика", draft.overrides.customerAddress, (v) => onField("overrides", "customerAddress", v), doc.customer.address)}
            </Section>

            {validation.scene ? (
                <div className="order-blank-AO__scene" role="alert" style={{ border: '1px solid var(--ui-danger, #dc2626)', borderRadius: 8, padding: 12 }}>
                    <div><b>Сцена и комплект требуют согласования</b><div>{validation.scene}</div></div>
                    {typeof onOpenScene === "function" ? <button type="button" className="order-doc-AO__ghost" onClick={onOpenScene}>Открыть калькулятор</button> : null}
                </div>
            ) : null}
            <Section title="Лицевая сторона и надпись" summary={summary.front} invalid={!!validation.sharedSurname}>
                <div className="order-blank-AO__group is-wide">
                    <span className="order-blank-AO__group-title">Фото камня</span>
                    <ImagePicker officeId={officeId} doc={doc} value={blank.front.image} onChange={(v) => setIn("front", "image", v)} />
                    {sceneLinked && blank.front.image?.type === "scene" ? (
                        <small className="order-blank-AO__note">Надписи уже нанесены на снимок 3D. Управление наложением ниже относится только к печатному листу и не перемещает объекты 3D. Настройки реального оформления пока изменяются в калькуляторе.</small>
                    ) : null}
                    <div className="order-blank-AO__inline">
                        <label className="order-doc-AO__check">
                            <input type="checkbox" checked={!!blank.front.overlay} onChange={(event) => setIn("front", "overlay", event.target.checked)} />
                            <span>ФИО и даты прямо на камне</span>
                        </label>
                        <label className="order-doc-AO__check">
                            <input type="checkbox" checked={!!blank.backEnabled} onChange={(event) => set("backEnabled", event.target.checked)} />
                            <span>Задняя стенка</span>
                        </label>
                        {doc.sceneShots && (blank.front.image?.type === "scene" || blank.back.image?.type === "scene") ? (
                            <label className="order-doc-AO__check" title="Размер камня, керамики, портрета, креста и надписей, отступы от верха и края">
                                <input type="checkbox" checked={!!blank.shotDims} onChange={(event) => set("shotDims", event.target.checked)} />
                                <span>Размеры на снимке</span>
                            </label>
                        ) : null}
                    </div>
                </div>

                <div className="order-blank-AO__group is-wide">
                    <span className="order-blank-AO__group-title">Кто на камне</span>
                    <div className="order-blank-AO__modes">
                        {PERSON_MODES.map(([key, label]) => (
                            <button type="button" key={key} className={(blank.persons || "single") === key ? "is-active" : ""} disabled={sceneLinked} onClick={() => set("persons", key)}>{label}</button>
                        ))}
                    </div>
                    {sceneLinked ? <small className="is-wide">Количество надписей меняется в калькуляторе вместе с их размещением.</small> : null}
                    {personsCount > 1 ? <div className="is-wide">
                        <label className="order-doc-AO__check"><input type="checkbox" checked={!!blank.sharedSurnameEnabled} onChange={(event) => set("sharedSurnameEnabled", event.target.checked)} /><span>Одна общая фамилия</span></label>
                        {blank.sharedSurnameEnabled ? input("Общая фамилия", blank.sharedSurname || "", (value) => set("sharedSurname", value), "Смирновы", { field: "sharedSurname" }) : null}
                    </div> : null}
                    {[0, 1, 2].filter((n) => n < personsCount).map((n) => {
                        const person = n === 0 ? draft.deceased : (blank.extraPersons || [])[n - 1] || {};
                        const change = (key) => (v) => (n === 0 ? onField("deceased", key, v) : setPerson(n - 1, key, v));
                        return (
                            <div key={n} className="order-blank-AO__person-row">
                                {personsCount > 1 ? <b>{n + 1}-й</b> : null}
                                {!blank.sharedSurnameEnabled || personsCount === 1 ? input("Фамилия", person.surname || "", change("surname")) : null}
                                {input("Имя", person.name || "", change("name"))}
                                {input("Отчество", person.patronymic || "", change("patronymic"))}
                                {input("Даты (Enter — новая строка)", person.dates || "", change("dates"), "12.03.1950 – 05.10.2024", { rows: 2 })}
                            </div>
                        );
                    })}
                    <div className="order-blank-AO__person-row is-epitaph">
                        <div>
                            {input("Эпитафия (общая)", draft.deceased.epitaph, (v) => onField("deceased", "epitaph", v), "", { rows: 2 })}
                            <small className="order-blank-AO__note">
                                Знаков без пробелов: <b>{epitaphChars(draft.deceased.epitaph).letters}</b> · всего: {epitaphChars(draft.deceased.epitaph).total}
                            </small>
                        </div>
                        {blank.backEnabled ? (
                            <label>
                                <span>Эпитафия на стороне</span>
                                <select value={blank.epitaphSide} onChange={(event) => set("epitaphSide", event.target.value)}>
                                    <option value="front">Лицевой</option>
                                    <option value="back">Задней</option>
                                </select>
                            </label>
                        ) : null}
                    </div>
                </div>

                <div className={`order-blank-AO__group is-wide ${blank.front.overlay ? "" : "is-disabled"}`}>
                    <span className="order-blank-AO__group-title">Как выглядит надпись</span>
                    <div className="order-blank-AO__inline">
                        <label>
                            <span>Шрифт ФИО и дат</span>
                            <select value={blank.front.font || "roboto"} onChange={(event) => setIn("front", "font", event.target.value)}>
                                {STONE_FONTS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Шрифт эпитафии</span>
                            <select value={blank.front.epitaphFont || ""} onChange={(event) => setIn("front", "epitaphFont", event.target.value)}>
                                <option value="">Как у ФИО</option>
                                {STONE_FONTS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Цвет</span>
                            <select value={blank.front.overlayColor} onChange={(event) => setIn("front", "overlayColor", event.target.value)}>
                                <option value="#ffffff">Белый (тёмный камень)</option>
                                <option value="#d4af37">Золото</option>
                                <option value="#111827">Чёрный (светлый камень)</option>
                            </select>
                        </label>
                    </div>
                    <div className="order-blank-AO__typo">
                        <span />
                        <b>Размер, пт</b>
                        <b>Межстрочное</b>
                        <b>Межбуквенное, пт</b>
                        <span>Фамилия</span>
                        <Num value={typo.surname} step={0.5} min={5} max={48} onChange={(v) => setIn("front", "surnameSize", v)} />
                        <Num value={typo.fioLine} step={0.05} min={0.8} max={2.5} onChange={(v) => setIn("front", "fioLine", v)} />
                        <Num value={typo.fioLetter} step={0.25} min={-1} max={8} onChange={(v) => setIn("front", "fioLetter", v)} />
                        <span>Имя, отчество</span>
                        <Num value={typo.name} step={0.5} min={5} max={40} onChange={(v) => setIn("front", "nameSize", v)} />
                        <small>как у фамилии</small>
                        <small>как у фамилии</small>
                        <span>Даты</span>
                        <Num value={typo.dates} step={0.5} min={4} max={32} onChange={(v) => setIn("front", "datesSize", v)} />
                        <Num value={typo.datesLine} step={0.05} min={0.8} max={2.5} onChange={(v) => setIn("front", "datesLine", v)} />
                        <Num value={typo.datesLetter} step={0.25} min={-1} max={8} onChange={(v) => setIn("front", "datesLetter", v)} />
                        <span>Эпитафия</span>
                        <Num value={typo.epitaph} step={0.5} min={4} max={32} onChange={(v) => setIn("front", "epitaphSize", v)} />
                        <Num value={typo.epitaphLine} step={0.05} min={0.8} max={2.5} onChange={(v) => setIn("front", "epitaphLine", v)} />
                        <Num value={typo.epitaphLetter} step={0.25} min={-1} max={8} onChange={(v) => setIn("front", "epitaphLetter", v)} />
                    </div>
                    <small className="order-blank-AO__note">Размер — в пунктах, как на листе при 100%. Если надпись не влезает по ширине камня, она сама уменьшится.</small>
                </div>

                <div className="order-blank-AO__group is-wide">
                    <span className="order-blank-AO__group-title">Положение</span>
                    <div className="order-blank-AO__pads">
                        <PhotoPad side={blank.front} onPatch={patchSide("front")} />
                        <TextPad side={blank.front} disabled={!blank.front.overlay} multi={personsCount > 1} onPatch={patchSide("front")} />
                    </div>
                    {officeId ? (
                        <div className="order-blank-AO__defaults">
                            <button type="button" className="order-doc-AO__ghost" onClick={saveDefaults}>Запомнить для всех бланков</button>
                            <small>{defaultsState}</small>
                        </div>
                    ) : null}
                </div>

                {input("Над фото (крест, символ…)", blank.front.text, (v) => setIn("front", "text", v), "", { wide: true })}
            </Section>

            {blank.backEnabled ? (
                <Section title="Задняя стенка" summary={summary.back}>
                    <label>
                        <span>Заголовок блока</span>
                        <select value={blank.back.title} onChange={(event) => setIn("back", "title", event.target.value)}>
                            <option value="Оборотная сторона">Оборотная сторона</option>
                            <option value="Надгробная плита">Надгробная плита</option>
                        </select>
                    </label>
                    <div className="is-wide">
                        <ImagePicker officeId={officeId} doc={doc} value={blank.back.image} onChange={(v) => setIn("back", "image", v)} />
                        <small className="order-blank-AO__note">
                            Фото товара — это вид спереди. Настоящую заднюю сторону показывает только 3D-сцена: запустите её и сделайте снимок «Зад».
                        </small>
                    </div>
                    {blank.back.image?.type && blank.back.image.type !== "none" ? (
                        <div className="order-blank-AO__pads is-wide">
                            <PhotoPad side={blank.back} onPatch={patchSide("back")} />
                            <TextPad side={blank.back} title="Текст оборота" defaultY={50} onPatch={patchSide("back")} />
                        </div>
                    ) : null}
                    {input("Текст / изображение на обороте", blank.back.text, (v) => setIn("back", "text", v), "Крест, ветка, текст…", { rows: 3, wide: true })}
                </Section>
            ) : null}

            {(doc.fences || []).length ? (
                <Section title="Ограда" summary={doc.fences.map((fence) => [fence.title, fence.meters ? `${fence.meters} м` : ""].filter(Boolean).join(" ")).join(" · ")}>
                    {doc.fences.map((fence) => {
                        const manual = (blank.fences || {})[fence.key] || {};
                        return (
                            <div key={fence.key} className="order-blank-AO__group is-wide">
                                <span className="order-blank-AO__group-title">
                                    {fence.title}{fence.article ? ` · арт. ${fence.article}` : ""} · {[fence.length && fence.width ? `${fence.length}×${fence.width} мм` : fence.size, fence.height ? `выс. ${fence.height} мм` : "", fence.meters ? `${fence.meters} м` : ""].filter(Boolean).join(" · ")}
                                </span>
                                <div className="order-blank-AO__pics">
                                    <button type="button" className={!manual.shotId ? "is-active" : ""} onClick={() => setFence(fence.key, "shotId", "")}>
                                        {fence.picture ? <img src={pictureUrl(fence.picture)} alt={fence.title} /> : <span>Фото товара</span>}
                                    </button>
                                    {doc.shots.map((shot) => (
                                        <button type="button" key={shot.id} className={manual.shotId === shot.id ? "is-active" : ""} title={shot.title} onClick={() => setFence(fence.key, "shotId", shot.id)}>
                                            <ShotImage officeId={officeId} shot={shot} />
                                            <small>3D</small>
                                        </button>
                                    ))}
                                </div>
                                <div className="order-blank-AO__person-row">
                                    {input("Ножки (столбы), шт", manual.posts || "", (v) => setFence(fence.key, "posts", v))}
                                    {input("Калитка", manual.gate || "", (v) => setFence(fence.key, "gate", v), "есть / нет")}
                                    {input("Где калитка", manual.gateSide || "", (v) => setFence(fence.key, "gateSide", v), "спереди по центру")}
                                    {input("Куда открывается", manual.gateOpen || "", (v) => setFence(fence.key, "gateOpen", v), "наружу / внутрь, влево / вправо")}
                                    {input("Ширина калитки, мм", manual.gateWidth || "", (v) => setFence(fence.key, "gateWidth", v))}
                                </div>
                                {input("Примечание", manual.note || "", (v) => setFence(fence.key, "note", v), "", { rows: 2, wide: true })}
                            </div>
                        );
                    })}
                    <small className="order-blank-AO__note is-wide">Ограда печатается отдельным листом: без цен, с размерами, цветом и артикулом. Картинка — снимок сцены сверху или фото товара.</small>
                </Section>
            ) : null}

            {validation.configuration ? (
                <div className="order-blank-AO__scene" role="alert" style={{ border: '1px solid var(--ui-danger, #dc2626)', borderRadius: 8, padding: 12 }}>
                    <div><b>Комплект не подтверждён</b><div>{validation.configuration}</div></div>
                    {typeof onOpenScene === "function" ? <button type="button" className="order-doc-AO__ghost" onClick={onOpenScene}>Перейти к комплекту</button> : null}
                </div>
            ) : null}
            <Section title="Наименование стелы и размеры" summary={summary.stela} invalid={!!validation.configuration}>
                {sceneLinked ? <div className="is-wide">Размеры и состав связаны со сценой. <button type="button" className="order-doc-AO__link" onClick={onOpenScene}>Изменить в калькуляторе</button></div> : null}
                {input("Наименование стелы", blank.title, (v) => set("title", v))}
                {input("Артикул", blank.article || "", (v) => set("article", v))}
                <div className="order-blank-AO__table is-wide">
                    <div className="is-head"><span>Наименование</span><span>Размеры</span><span>Полировка</span><span>Примечание</span><span /></div>
                    {blank.components.map((row, index) => (
                        <div key={index}>
                            <input value={row.name} onChange={(event) => setRow(index, "name", event.target.value)} />
                            <input readOnly={sceneLinked} value={row.size} placeholder="1000×500×50" onChange={(event) => setRow(index, "size", event.target.value)} />
                            <input value={row.polish} placeholder="5 ст." onChange={(event) => setRow(index, "polish", event.target.value)} />
                            <input value={row.note} onChange={(event) => setRow(index, "note", event.target.value)} />
                            <button type="button" disabled={sceneLinked} title="Убрать строку" onClick={() => set("components", blank.components.filter((_, i) => i !== index))}>×</button>
                        </div>
                    ))}
                    <button type="button" className="order-doc-AO__link" disabled={sceneLinked || blank.components.length >= 14} onClick={() => set("components", [...blank.components, { name: "", size: "", polish: "", note: "" }])}>
                        + строка
                    </button>
                </div>
                <div className="order-doc-AO__checks is-wide">
                    {BLANK_CHECKS.map(([key, label]) => (
                        <label key={key} className="order-doc-AO__check">
                            <input type="checkbox" checked={!!blank.checks[key]} onChange={(event) => setIn("checks", key, event.target.checked)} />
                            <span>{label}</span>
                        </label>
                    ))}
                </div>
            </Section>

            <Section title="Оформление и дополнения" summary={summary.options}>
                {BLANK_OPTIONS.map(([key, label]) => <React.Fragment key={key}>{input(label, blank.options[key] || "", (v) => setIn("options", key, v))}</React.Fragment>)}
                <label className="is-wide">
                    <span>Дополнительно — одна строка = один пункт (ваза, лавка, буквы…)</span>
                    <textarea rows={3} value={blank.extras.join("\n")} onChange={(event) => set("extras", event.target.value.split("\n"))} />
                </label>
            </Section>

            {React.createElement(checkout ? ConfirmSection : Section, {
                title: "Сроки и место", summary: summary.dates, invalid: !!(validation.installPlace || validation.acceptedBy || validation.datesSection),
                ...(checkout ? {
                    confirmed: sectionConfirmed(draft, "dates"), touched: !!draft.sections?.dates?.signature,
                    onConfirm: () => {
                        if (draft.checkout?.installWork !== "none" && !String(blank.installPlace || draft.overrides?.cemeteryName || "").trim()) return "Укажите место установки";
                        if (!String(draft.acceptedBy || "").trim()) return "Укажите, кто принял заказ";
                        onField("sections", "dates", { signature: datesSignature(draft), confirmedAt: Date.now() });
                        return "";
                    },
                } : {}),
            }, <>
                <label><span>Дата установки</span><TaskDueDatePicker mode="date" title="Дата установки" value={blank.installPeriod} onChange={(value) => { set("installPeriod", value ? value.split("-").reverse().join(".") : ""); }} /></label>
                <label><span>Плановый месяц показа</span><TaskDueDatePicker mode="month" title="Плановый месяц показа" value={blank.showMonth} onChange={(value) => set("showMonth", value ? new Date(`${value}-01T12:00:00`).toLocaleDateString("ru-RU", { month: "long", year: "numeric" }).replace(/\s*г\.$/, "") : "")} /></label>
                {input("Место установки", blank.installPlace, (v) => set("installPlace", v), [doc.object.cemetery, doc.object.sector, doc.object.plot].filter(Boolean).join(", "), { field: "installPlace" })}
                {checkout ? <>
                    {input("Сектор", draft.overrides.sector || "", (v) => onField("overrides", "sector", v))}
                    {input("Участок", draft.overrides.plot || "", (v) => onField("overrides", "plot", v))}
                    {input("Место / ориентир", draft.overrides.burialPlace || "", (v) => onField("overrides", "burialPlace", v))}
                    {input("Геометка / описание", draft.overrides.geoLabel || "", (v) => onField("overrides", "geoLabel", v))}
                    {input("Широта", draft.overrides.geoLat || "", (v) => onField("overrides", "geoLat", v))}
                    {input("Долгота", draft.overrides.geoLng || "", (v) => onField("overrides", "geoLng", v))}
                    {input("Комментарий к заказу", draft.overrides.comment || "", (v) => onField("overrides", "comment", v), "", { rows: 3, wide: true })}
                </> : null}
                {input("Место доставки", blank.deliveryPlace, (v) => set("deliveryPlace", v))}
                {input("Заказ принял", draft.acceptedBy, (v) => onField(null, "acceptedBy", v), "", { field: "acceptedBy" })}
                <label className="order-doc-AO__check">
                    <input type="checkbox" checked={!!blank.showPrices} onChange={(event) => set("showPrices", event.target.checked)} />
                    <span>Печатать цены и итог</span>
                </label>
            </>)}

            {checkout ? (
                <ConfirmSection
                    title="Работы на месте"
                    summary={worksSummary}
                    invalid={!!(validation.installWork || validation.worksSection)}
                    confirmed={sectionConfirmed(draft, "works")}
                    touched={!!draft.sections?.works?.signature}
                    onConfirm={() => {
                        const works = draft.checkout?.installationWorks || [];
                        const mode = draft.checkout?.installWork || "";
                        if (!mode && !works.some((work) => work.status === "confirmed")) return "Выберите тип работ или отметьте «Без установки»";
                        if (works.some((work) => work.status === "pending")) return "Подтвердите или отклоните все предложенные работы";
                        const issue = (doc.installation?.issues || []).find((entry) => entry.level === "error");
                        if (issue) return issue.message;
                        onField("sections", "works", { signature: worksSignature(draft), confirmedAt: Date.now() });
                        return "";
                    }}
                >
                    <SmartInstallationWorks doc={doc} draft={draft} onField={onField} validation={validation} />
                </ConfirmSection>
            ) : (
                <Section title="Работы на месте" summary={worksSummary}>
                    <div className="order-blank-AO__group is-wide">
                        <span className="order-blank-AO__group-title">Шаблон монтажных работ из каталога офиса</span>
                        <label className="is-wide"><span>Выбрать работу (информация для бланка, не добавляет стоимость в заказ)</span>
                            <select value={blank.installationTemplateId || ""} onChange={(event) => setInstallationTemplate(event.target.value)}>
                                <option value="">Без шаблона</option>
                                {installationCatalog.map((work) => <option key={work.id} value={work.id}>{work.article} — {work.name}</option>)}
                            </select>
                        </label>
                        {installationCatalogError ? <small role="alert">{installationCatalogError}</small> : null}
                        <small className="order-blank-AO__note">Подтверждённые работы WRK уже в позициях заказа. Шаблон здесь — только справочная строка описания PDF.</small>
                    </div>
                </Section>
            )}
        </div>
    );
}
